/**
 * Farm Category Constants for Scope Certificates
 * 
 * These constants define the different categories of process codes used in facility processing
 * and farm capacity calculations throughout the scope certificate system.
 */

// Core farm process codes
export const FARM_CODES = [
  "PR0011", // Farm process code 1
  "PR0036", // Farm process code 2  
  "PR0037", // Farm process code 3
  "PR0038", // Farm process code 4
] as const;

// Supply chain process codes
export const SUPPLY_CHAIN_CODES = [
  "PR0002", // Supply chain process code 1
  "PR0014", // Supply chain process code 2
  "PR0026", // Supply chain process code 3
] as const;

// Exempt combo codes (processes that don't require certain validations)
export const EXEMPT_COMBO_CODES = [
  "PR0000", // Exempt combo code 1
  "PR0030", // Exempt combo code 2
  "PR0048", // Exempt combo code 3
] as const;

// All farm-related categories (farm codes + supply chain codes)
export const ALL_FARM_CATEGORIES = [
  ...FARM_CODES,
  ...SUPPLY_CHAIN_CODES,
] as const;

// Type definitions for better TypeScript support
export type FarmCode = typeof FARM_CODES[number];
export type SupplyChainCode = typeof SUPPLY_CHAIN_CODES[number];
export type ExemptComboCode = typeof EXEMPT_COMBO_CODES[number];
export type FarmCategory = typeof ALL_FARM_CATEGORIES[number];

// Helper functions for common operations
export const FarmCategoryUtils = {
  /**
   * Check if a process code is a farm code
   */
  isFarmCode: (code: string): code is FarmCode => {
    return FARM_CODES.includes(code as FarmCode);
  },

  /**
   * Check if a process code is a supply chain code
   */
  isSupplyChainCode: (code: string): code is SupplyChainCode => {
    return SUPPLY_CHAIN_CODES.includes(code as SupplyChainCode);
  },

  /**
   * Check if a process code is an exempt combo code
   */
  isExemptComboCode: (code: string): code is ExemptComboCode => {
    return EXEMPT_COMBO_CODES.includes(code as ExemptComboCode);
  },

  /**
   * Check if a process code is any farm-related category
   */
  isFarmCategory: (code: string): code is FarmCategory => {
    return ALL_FARM_CATEGORIES.includes(code as FarmCategory);
  },

  /**
   * Get all farm codes as a Set for efficient lookups
   */
  getFarmCodesSet: (): Set<string> => new Set(FARM_CODES),

  /**
   * Get all supply chain codes as a Set for efficient lookups
   */
  getSupplyChainCodesSet: (): Set<string> => new Set(SUPPLY_CHAIN_CODES),

  /**
   * Get all exempt combo codes as a Set for efficient lookups
   */
  getExemptComboCodesSet: (): Set<string> => new Set(EXEMPT_COMBO_CODES),

  /**
   * Get all farm categories as a Set for efficient lookups
   */
  getAllFarmCategoriesSet: (): Set<string> => new Set(ALL_FARM_CATEGORIES),
};
